import { useState, FormEvent } from 'react'
import { useAuth } from '../hooks/useAuth'
import { ApiError } from '../api/client'

function Login() {
  const { login, environment } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // ============================================
  // Handle Login Submit
  // ============================================
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Validate inputs
    if (!email || !password) {
      setError('이메일과 비밀번호를 입력해주세요')
      return
    }

    setIsLoggingIn(true)
    setError(null)

    try {
      await login(email, password)
      // Navigation handled by AuthContext
    } catch (err) {
      console.error('Login error:', err)

      if (err instanceof ApiError) {
        // Map error codes to user-friendly messages
        switch (err.code) {
          case 'INVALID_CREDENTIALS':
          case 'LOGIN_FAILED':
            setError('이메일 또는 비밀번호가 올바르지 않습니다')
            break
          case 'NO_TOKEN':
            setError('인증 토큰을 받지 못했습니다. 잠시 후 다시 시도해주세요')
            break
          case 'NETWORK_ERROR':
            setError('네트워크 연결을 확인해주세요')
            break
          default:
            setError(err.message || '로그인에 실패했습니다')
        }
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('알 수 없는 오류가 발생했습니다')
      }
    } finally {
      setIsLoggingIn(false)
    }
  }

  // ============================================
  // Render
  // ============================================
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 bg-white rounded-lg shadow-lg">
        {/* Title */}
        <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
          ABC POS 로그인
        </h1>

        {/* Environment Indicator (fixed at build time) */}
        <div
          className={`mb-6 p-3 rounded-lg text-center font-semibold border ${
            environment === 'development'
              ? 'bg-red-100 text-red-800 border-red-300'
              : 'bg-blue-50 text-blue-800 border-blue-200'
          }`}
        >
          {environment === 'development' ? '개발 환경' : '운영 환경'}
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              이메일
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@abcpay.co.kr"
              disabled={isLoggingIn}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoComplete="email"
            />
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              비밀번호
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호를 입력하세요"
              disabled={isLoggingIn}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              autoComplete="current-password"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoggingIn}
            className="w-full py-3 px-4 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:bg-blue-400 disabled:cursor-not-allowed"
          >
            {isLoggingIn ? '로그인 중...' : '로그인'}
          </button>
        </form>

        {/* Footer Info */}
        <div className="mt-6 text-center text-xs text-gray-500">
          <p>ABC POS v1.0.0</p>
        </div>
      </div>
    </div>
  )
}

export default Login
